import { z } from 'zod';
import { WRITING_ADDRESS_FORMS, WRITING_EMOJI_USES, WRITING_HASHTAG_USES, WRITING_HUMOURS, WRITING_NARRATIVE_VOICES, WRITING_PARAGRAPH_STYLES, WRITING_SENTENCE_LENGTHS, WRITING_STYLE_LIMITS, } from '../writing-style.js';
/**
 * `feed_analyst` (spec 80 section 4.14, S200-1, REQ-80-025..027): reads the
 * captions of the tenant's own published posts and proposes a writing style
 * (rules, phrase lists, fixed hashtags, signature) and sample posts, each with
 * the posts it relies on. Captions are style material only, never knowledge
 * (REQ-20-024). The run has no effect: every proposal waits for a person.
 */
export const FEED_ANALYST_LIMITS = {
    /** Posts read per account and kept for the context, newest first. */
    perAccountLimit: 50,
    maxPosts: 50,
    /** Fewer usable posts fail the run `too_few_posts` before any provider call. */
    minPosts: 5,
    captionMaxLength: 5000,
    summaryMaxLength: 800,
    evidenceMin: 2,
    evidenceMax: 5,
    /** A fixed hashtag needs more evidence than a phrase: it has to be a habit. */
    hashtagEvidenceMin: 3,
    maxOpeners: WRITING_STYLE_LIMITS.maxOpeners,
    maxSignOffs: WRITING_STYLE_LIMITS.maxSignOffs,
    maxFavouritePhrases: WRITING_STYLE_LIMITS.maxFavouritePhrases,
    maxFixedHashtags: WRITING_STYLE_LIMITS.maxFixedHashtags,
    openerMaxLength: WRITING_STYLE_LIMITS.openerMaxLength,
    favouritePhraseMaxLength: WRITING_STYLE_LIMITS.favouritePhraseMaxLength,
    hashtagMaxLength: 100,
    signatureMaxLength: WRITING_STYLE_LIMITS.signatureMaxLength,
    maxSamples: WRITING_STYLE_LIMITS.maxSamples,
    sampleMinLength: 40,
    sampleMaxLength: WRITING_STYLE_LIMITS.sampleMaxLength,
    reasonMaxLength: 200,
};
/** A post reference of the context: `p1` is the newest post. */
export const FEED_POST_REF_PATTERN = /^p[1-9][0-9]?$/;
export const feedAnalystInputSchema = z.strictObject({ property_id: z.uuid() });
const ref = z.string().regex(FEED_POST_REF_PATTERN);
const evidence = (min) => z.array(ref).min(min).max(FEED_ANALYST_LIMITS.evidenceMax);
const text = (max) => z.string().trim().min(1).max(max);
const choice = (values) => z.object({ value: z.enum(values), evidence: evidence(FEED_ANALYST_LIMITS.evidenceMin) }).nullable();
const phrases = (maxLength, maxItems) => z.array(z.object({ value: text(maxLength), evidence: evidence(FEED_ANALYST_LIMITS.evidenceMin) })).max(maxItems);
/** The scalar style fields a run may propose, in display order. */
export const FEED_STYLE_CHOICE_FIELDS = ['address_form', 'emoji_use', 'hashtag_use', 'paragraph_style', 'narrative_voice', 'sentence_length', 'humour'];
/** The list style fields a run may propose (`avoid_phrases` never: absence in a feed is no evidence). */
export const FEED_STYLE_LIST_FIELDS = ['openers', 'sign_offs', 'favourite_phrases', 'fixed_hashtags'];
export const feedAnalystOutputSchema = z.object({
    summary: text(FEED_ANALYST_LIMITS.summaryMaxLength),
    style: z.object({
        address_form: choice(WRITING_ADDRESS_FORMS),
        emoji_use: choice(WRITING_EMOJI_USES),
        hashtag_use: choice(WRITING_HASHTAG_USES),
        paragraph_style: choice(WRITING_PARAGRAPH_STYLES),
        narrative_voice: choice(WRITING_NARRATIVE_VOICES),
        sentence_length: choice(WRITING_SENTENCE_LENGTHS),
        humour: choice(WRITING_HUMOURS),
        openers: phrases(FEED_ANALYST_LIMITS.openerMaxLength, FEED_ANALYST_LIMITS.maxOpeners),
        sign_offs: phrases(FEED_ANALYST_LIMITS.openerMaxLength, FEED_ANALYST_LIMITS.maxSignOffs),
        favourite_phrases: phrases(FEED_ANALYST_LIMITS.favouritePhraseMaxLength, FEED_ANALYST_LIMITS.maxFavouritePhrases),
        fixed_hashtags: z
            .array(z.object({
            value: z.string().regex(/^[\p{L}\p{N}_]{1,100}$/u),
            evidence: evidence(FEED_ANALYST_LIMITS.hashtagEvidenceMin),
        }))
            .max(FEED_ANALYST_LIMITS.maxFixedHashtags),
        signature: z.object({ value: text(FEED_ANALYST_LIMITS.signatureMaxLength), evidence: evidence(FEED_ANALYST_LIMITS.evidenceMin) }).nullable(),
    }),
    samples: z.array(z.object({ ref, reason: text(FEED_ANALYST_LIMITS.reasonMaxLength) })).max(FEED_ANALYST_LIMITS.maxSamples),
});
/** Every style field a run may propose, as the model names it in the flat answer shape. */
export const FEED_PROPOSAL_FIELDS = [...FEED_STYLE_CHOICE_FIELDS, ...FEED_STYLE_LIST_FIELDS, 'signature'];
/**
 * What the model is asked for (prompt v2): one flat list of proposals, each `{field, value, evidence}`,
 * instead of eleven differently shaped, nullable style fields. Anthropic compiles a strict tool schema
 * into a grammar and rejects a large one ("The compiled grammar is too large"); the nested shape with
 * eight `object | null` unions, seven enums and four arrays of objects was over that limit. Values and
 * bounds are checked by `feedAnalystOutputSchema` after `normalizeFeedAnalystAnswer` has regrouped them.
 */
export const feedAnalystModelSchema = z.object({
    summary: z.string(),
    proposals: z.array(z.object({ field: z.enum(FEED_PROPOSAL_FIELDS), value: z.string(), evidence: z.array(z.string()) })),
    samples: z.array(z.object({ ref: z.string(), reason: z.string() })),
});
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
/**
 * Turns the flat model answer into `feedAnalystOutputSchema`'s shape: the first proposal of a scalar
 * field or the signature wins, list proposals keep their order, a missing field is null or empty. An
 * answer already in the stored shape (`style` present) and anything that is not an object are returned
 * unchanged for the schema to accept or reject.
 */
export function normalizeFeedAnalystAnswer(raw) {
    if (!isRecord(raw) || 'style' in raw || !Array.isArray(raw['proposals']))
        return raw;
    const entries = raw['proposals'].filter(isRecord).map((entry) => ({ field: entry['field'], value: entry['value'], evidence: entry['evidence'] }));
    const forField = (field) => entries.filter((entry) => entry.field === field).map(({ value, evidence }) => ({ value, evidence }));
    const style = {};
    for (const field of FEED_STYLE_CHOICE_FIELDS)
        style[field] = forField(field)[0] ?? null;
    for (const field of FEED_STYLE_LIST_FIELDS)
        style[field] = forField(field);
    style['signature'] = forField('signature')[0] ?? null;
    return { summary: raw['summary'], style, samples: raw['samples'] };
}
/** Case-insensitive, whitespace-normalised form used to compare phrases and captions. */
export function normalizeFeedText(value) {
    return value.normalize('NFC').replace(/\s+/gu, ' ').trim().toLocaleLowerCase('de');
}
/**
 * The proposal keys of a stored `feed_analyst` output (spec 80 section 4.14):
 * `style.<field>` for a scalar and the signature, `style.<field>.<index>` for a
 * list entry, `sample.<ref>` for a sample.
 */
export function feedAnalystProposalKeys(stored) {
    const keys = [];
    for (const field of FEED_STYLE_CHOICE_FIELDS)
        if (stored.style[field])
            keys.push(`style.${field}`);
    for (const field of FEED_STYLE_LIST_FIELDS)
        stored.style[field].forEach((_entry, index) => keys.push(`style.${field}.${index}`));
    if (stored.style.signature)
        keys.push('style.signature');
    for (const sample of stored.samples)
        keys.push(`sample.${sample.ref}`);
    return keys;
}
/** A person's decision on one proposal (spec 80 section 4.14 `ai_proposal_decisions`). */
export const AI_PROPOSAL_DECISIONS = ['accepted', 'edited', 'rejected'];
export const AI_PROPOSAL_DECISION_LIMITS = {
    maxDecisions: 60,
    proposalKeyMaxLength: 120,
};
//# sourceMappingURL=feed-analyst.js.map